<template>
  <div ref="containerRef" class="tabs-container" role="tablist">
    <button
      v-for="(tab, index) in tabs"
      :id="`tab-${tab}`"
      :key="tab"
      :ref="
        (el) => {
          if (el) tabRefs[index] = el as HTMLElement;
        }
      "
      type="button"
      class="tab-button"
      role="tab"
      :aria-selected="modelValue === tab"
      :aria-controls="`tabpanel-${tab}`"
      :class="{ 'tab-button--active': modelValue === tab }"
      @click="$emit('update:modelValue', tab)"
    >
      <slot name="tab-item" :tab="tab">
        {{ tab }}
      </slot>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';

const props = withDefaults(
  defineProps<{
    tabs: string[];
    modelValue: string;
    activeTabIntoViewOnMounted?: boolean;
  }>(),
  {
    activeTabIntoViewOnMounted: true,
  },
);

defineEmits<(e: 'update:modelValue', value: string) => void>();

const containerRef = ref<HTMLElement | null>(null);
const tabRefs = ref<HTMLElement[]>([]);

const scrollActiveTabIntoView = (tabName: string) => {
  const index = props.tabs.indexOf(tabName);
  const element = tabRefs.value[index];

  if (index !== 0 && element && containerRef.value) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start',
    });
  }
};

onMounted(() => {
  if (props.modelValue && props.activeTabIntoViewOnMounted) {
    scrollActiveTabIntoView(props.modelValue);
  }
});
</script>

<style lang="scss" scoped>
.tabs-container {
  display: flex;

  // Scrollable logic
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
